import React, { useState, useEffect, useCallback } from 'react';
import BBALoader from '../components/BBALoader';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, ChevronUp, X, Trash2, Plus, Upload, FileText, PlayCircle, BookOpen, Sparkles } from 'lucide-react';
import {
  getCourseById,
  updateCourse,
  deleteCourse,
  updateModule,
  deleteModule,
  addModuleToCourse,
  reorderModules,
  addLessonToModule,
  updateLesson,
  deleteLesson,
  reorderLessons
} from '../services/firebase/courses';
import { generateLessonDescription } from '../services/hrAiService';
import { useTheme } from '../contexts/ThemeContext';
import BackButton from '../components/BackButton';
import EditButton from '../components/EditButton';
import SaveButton from '../components/SaveButton';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { getAuth } from 'firebase/auth';
import PortalBackground from '../components/PortalBackground';
import CourseVideoUploader from '../components/crm/CourseVideoUploader';

const categoryOptions = ['General', 'Sales', 'Operations', 'Technology', 'Finance', 'Marketing', 'HR'];

// ---------- Shared upload helpers (ported from the old LessonOverview) ----------

const uploadDocumentToStorage = async (file) => {
  const auth = getAuth();
  if (!auth.currentUser) throw new Error('User not authenticated. Please log in again.');
  const safeName = String(file.name || 'file').replace(/[^\w.\-]+/g, '_');
  const storageRef = ref(storage, `lesson-documents/${Date.now()}_${safeName}`);
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
};

// ------------------------------- Lesson editor -------------------------------

const LessonEditor = ({ courseId, moduleId, lesson, courseTitle, darkMode, onSaved }) => {
  const [editMode, setEditMode] = useState(false);
  const [title, setTitle] = useState(lesson.title || '');
  const [description, setDescription] = useState(lesson.description || '');
  const [videoUrl, setVideoUrl] = useState(lesson.videoUrl || '');
  const [videoLength, setVideoLength] = useState(lesson.videoLength || '');
  const [videoUploading, setVideoUploading] = useState(false);
  const [documents, setDocuments] = useState(lesson.documents || []);
  const [backup, setBackup] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Keep in sync with the latest saved data whenever we're not actively editing.
  useEffect(() => {
    if (!editMode) {
      setTitle(lesson.title || '');
      setDescription(lesson.description || '');
      setVideoUrl(lesson.videoUrl || '');
      setVideoLength(lesson.videoLength || '');
      setDocuments(lesson.documents || []);
    }
  }, [lesson, editMode]);

  const handleEdit = () => {
    setBackup({ title, description, videoUrl, videoLength, documents });
    setEditMode(true);
  };

  const handleCancel = () => {
    setTitle(backup.title);
    setDescription(backup.description);
    setVideoUrl(backup.videoUrl);
    setVideoLength(backup.videoLength);
    setDocuments(backup.documents || []);
    setEditMode(false);
    setError(null);
  };

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    const newDocs = files.map((file) => ({
      name: file.name,
      displayName: file.name,
      size: file.size,
      type: file.type,
      uploadedAt: new Date().toISOString(),
      file
    }));
    setDocuments((prev) => [...prev, ...newDocs]);
    e.target.value = '';
  };

  const handleRemoveDocument = (index) => setDocuments((prev) => prev.filter((_, i) => i !== index));
  const handleDocumentNameChange = (index, newName) =>
    setDocuments((prev) => prev.map((doc, i) => (i === index ? { ...doc, displayName: newName } : doc)));

  const handleGenerateDescription = async () => {
    if (!description.trim()) {
      setAiError('Paste the class call summary into the description first.');
      return;
    }
    try {
      setAiLoading(true);
      setAiError(null);
      const { content } = await generateLessonDescription({ summary: description, lessonTitle: title, courseTitle });
      if (content) setDescription(content);
    } catch (err) {
      console.error('Error generating lesson description:', err);
      setAiError(err?.message || 'Failed to generate description. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const processedDocuments = [];
      for (const d of documents) {
        if (d.file instanceof File) {
          const url = await uploadDocumentToStorage(d.file);
          processedDocuments.push({ name: d.name, displayName: d.displayName || d.name, size: d.size, type: d.type, uploadedAt: d.uploadedAt, url });
        } else if (d.url) {
          processedDocuments.push({ name: d.name, displayName: d.displayName || d.name, size: d.size, type: d.type, uploadedAt: d.uploadedAt, url: d.url });
        } else {
          setError(`Document "${d.displayName || d.name}" has no download link. Remove it and upload the file again.`);
          setSaving(false);
          return;
        }
      }

      await updateLesson(courseId, moduleId, lesson.id, {
        title,
        description,
        videoUrl,
        videoLength,
        documents: processedDocuments,
        order: typeof lesson.order === 'number' ? lesson.order : 0,
        status: lesson.status || 'draft'
      });

      setEditMode(false);
      if (onSaved) await onSaved();
    } catch (err) {
      console.error('Error saving lesson:', err);
      setError('Failed to save lesson. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const fieldClass = `w-full border rounded-md px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`;
  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;

  return (
    <div className={`p-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="flex justify-end gap-2 mb-3">
        {!editMode ? (
          <EditButton onClick={handleEdit} />
        ) : (
          <>
            <button onClick={handleCancel} className="flex items-center gap-1 px-3 py-2 rounded-md bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
              <X className="h-4 w-4" /> Cancel
            </button>
            <SaveButton onClick={handleSave} disabled={saving || videoUploading} />
          </>
        )}
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      <div className="mb-4">
        <label className={labelClass}>Title</label>
        <input type="text" className={fieldClass} value={title} onChange={(e) => setTitle(e.target.value)} readOnly={!editMode} />
      </div>

      <div className="mb-4">
        <label className={labelClass}>
          Video{videoLength && <span className={`ml-2 font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{videoLength}</span>}
        </label>
        {editMode ? (
          <CourseVideoUploader
            courseId={courseId}
            lessonId={lesson.id}
            videoUrl={videoUrl}
            darkMode={darkMode}
            disabled={saving}
            onUploadingChange={setVideoUploading}
            onChange={(v) => {
              setVideoUrl(v.videoUrl);
              setVideoLength(v.videoLength);
            }}
          />
        ) : videoUrl ? (
          <video key={videoUrl} controls src={videoUrl} className="w-full rounded-md" style={{ maxHeight: '600px' }}>
            Your browser does not support the video tag.
          </video>
        ) : (
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No video uploaded yet.</p>
        )}
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <label className={labelClass.replace(' mb-1', '')}>Description</label>
          {editMode && (
            <button
              type="button"
              onClick={handleGenerateDescription}
              disabled={aiLoading}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                aiLoading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                  : 'bg-red-600 text-white hover:bg-red-700'
              }`}
              title="Paste the class call summary above, then generate a description"
            >
              <Sparkles className="h-4 w-4" />
              {aiLoading ? 'Generating…' : 'Use AI'}
            </button>
          )}
        </div>
        <textarea className={`${fieldClass} min-h-[120px]`} value={description} onChange={(e) => setDescription(e.target.value)} readOnly={!editMode} placeholder={editMode ? 'Paste the class call summary here, then click “Use AI” to transform it into a description.' : ''} />
        {aiError && <p className="text-sm text-red-600 mt-1">{aiError}</p>}
      </div>

      <div>
        <label className={labelClass}>Documents</label>
        {editMode ? (
          <>
            <input type="file" multiple accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx" className="hidden" id={`docs-${lesson.id}`} onChange={handleDocumentUpload} />
            <label htmlFor={`docs-${lesson.id}`} className={`inline-flex items-center gap-2 px-4 py-2 border rounded-md cursor-pointer ${darkMode ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-white border-gray-300 hover:bg-gray-50'}`}>
              <Upload className="h-4 w-4" /> Upload Documents
            </label>
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Supported: PDF, DOC, DOCX, TXT, XLS, XLSX, PPT, PPTX</p>
            {documents.length > 0 && (
              <div className="space-y-2 mt-3">
                {documents.map((doc, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-3 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <input type="text" className={`w-full border rounded-md px-2 py-1 text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`} value={doc.displayName} onChange={(e) => handleDocumentNameChange(idx, e.target.value)} placeholder="Display name" />
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Original: {doc.name}{doc.size ? ` (${(doc.size / 1024 / 1024).toFixed(2)} MB)` : ''}</p>
                      </div>
                    </div>
                    <button type="button" onClick={() => handleRemoveDocument(idx)} className="text-red-500 hover:text-red-700 flex-shrink-0 ml-2">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : documents.length === 0 ? (
          <p className={`italic text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No documents uploaded</p>
        ) : (
          <div className="space-y-2">
            {documents.map((doc, idx) => (
              <a key={idx} href={doc.url || undefined} target="_blank" rel="noreferrer" className={`flex items-center gap-2 p-3 rounded-md ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'}`}>
                <FileText className="h-4 w-4 text-gray-500" />
                <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{doc.displayName || doc.name}</span>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ------------------------------- Module accordion -------------------------------

const ModuleAccordion = ({
  courseId, courseTitle, module, index, total, darkMode,
  expanded, onToggle, onReorderModule,
  expandedLessons, onToggleLesson, onReorderLesson,
  onAddLesson, refresh
}) => {
  const [editMode, setEditMode] = useState(false);
  const [title, setTitle] = useState(module.title || '');
  const [backup, setBackup] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editMode) {
      setTitle(module.title || '');
    }
  }, [module, editMode]);

  const handleEdit = () => { setBackup({ title }); setEditMode(true); };
  const handleCancel = () => { setTitle(backup.title); setEditMode(false); };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateModule(courseId, module.id, { title, order: module.order || index + 1 });
      setEditMode(false);
      if (refresh) await refresh();
    } catch (err) {
      console.error('Error saving module:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this module and all its lessons?')) return;
    try {
      await deleteModule(courseId, module.id);
      if (refresh) await refresh();
    } catch (err) {
      console.error('Error deleting module:', err);
    }
  };

  const lessons = module.lessons || [];
  const labelClass = `block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const fieldClass = `w-full border rounded-md px-3 py-2 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`;

  return (
    <div className={`rounded-2xl border overflow-hidden border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm`}>
      {/* Module header */}
      <div className={`flex items-center gap-3 p-4 cursor-pointer ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`} onClick={() => onToggle(module.id)}>
        {expanded ? <ChevronDown className="h-5 w-5 flex-shrink-0 text-gray-400" /> : <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-400" />}
        <BookOpen className="h-5 w-5 flex-shrink-0 text-red-600" />
        <div className="flex-1 min-w-0">
          <span className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{module.title || 'Untitled Module'}</span>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{lessons.length} lesson{lessons.length === 1 ? '' : 's'}</p>
        </div>
        <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => onReorderModule(index, -1)} disabled={index === 0} className={`p-1 rounded ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-30' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-30'}`} title="Move module up">
            <ChevronUp className="h-4 w-4" />
          </button>
          <button onClick={() => onReorderModule(index, 1)} disabled={index === total - 1} className={`p-1 rounded ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-30' : 'bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-30'}`} title="Move module down">
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Module body */}
      {expanded && (
        <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} p-4`}>
          {/* Module detail fields */}
          <div className={`rounded-md border p-4 mb-4 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex justify-end gap-2 mb-3">
              {!editMode ? (
                <EditButton onClick={handleEdit} />
              ) : (
                <>
                  <button onClick={handleDelete} className="flex items-center gap-1 px-3 py-2 rounded-md bg-red-100 text-red-700 font-semibold hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800">
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                  <button onClick={handleCancel} className="flex items-center gap-1 px-3 py-2 rounded-md bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
                    <X className="h-4 w-4" /> Cancel
                  </button>
                  <SaveButton onClick={handleSave} disabled={saving} />
                </>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className={labelClass}>Module Title</label>
                <input type="text" className={fieldClass} value={title} onChange={(e) => setTitle(e.target.value)} readOnly={!editMode} />
              </div>
            </div>
          </div>

          {/* Lessons */}
          <div className="flex items-center justify-between mb-2">
            <h3 className={`text-sm font-semibold uppercase tracking-wide ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Lessons</h3>
            <button onClick={() => onAddLesson(module.id, lessons.length)} className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              <Plus className="h-4 w-4" /> Add Lesson
            </button>
          </div>

          {lessons.length === 0 ? (
            <p className={`text-sm italic px-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No lessons yet.</p>
          ) : (
            <div className="space-y-2">
              {lessons.map((lesson, lIdx) => {
                const isOpen = expandedLessons.includes(lesson.id);
                return (
                  <div key={lesson.id} className={`rounded-md border overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className={`flex items-center gap-3 p-3 cursor-pointer ${darkMode ? 'bg-gray-700/40 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'}`} onClick={() => onToggleLesson(lesson.id)}>
                      {isOpen ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-400" /> : <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />}
                      <PlayCircle className="h-4 w-4 flex-shrink-0 text-red-600" />
                      <span className={`flex-1 min-w-0 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{lesson.title || 'Untitled Lesson'}</span>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => onReorderLesson(module.id, lIdx, -1)} disabled={lIdx === 0} className={`p-1 rounded ${darkMode ? 'text-gray-400 hover:bg-gray-600 disabled:opacity-30' : 'text-gray-500 hover:bg-gray-200 disabled:opacity-30'}`} title="Move lesson up">
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <button onClick={() => onReorderLesson(module.id, lIdx, 1)} disabled={lIdx === lessons.length - 1} className={`p-1 rounded ${darkMode ? 'text-gray-400 hover:bg-gray-600 disabled:opacity-30' : 'text-gray-500 hover:bg-gray-200 disabled:opacity-30'}`} title="Move lesson down">
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {isOpen && (
                      <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <LessonEditor courseId={courseId} moduleId={module.id} lesson={lesson} courseTitle={courseTitle} darkMode={darkMode} onSaved={refresh} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ------------------------------- Course details -------------------------------

const CourseDetailsCard = ({ courseId, course, darkMode, onSaved, onDeleted }) => {
  const [editMode, setEditMode] = useState(false);
  const [title, setTitle] = useState(course.title || '');
  const [category, setCategory] = useState(course.category || 'General');
  const [description, setDescription] = useState(course.description || '');
  const [thumbnail, setThumbnail] = useState(course.thumbnailUrl || '');
  const [isPublished, setIsPublished] = useState(course.isPublished !== false);
  const [backup, setBackup] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editMode) {
      setTitle(course.title || '');
      setCategory(course.category || 'General');
      setDescription(course.description || '');
      setThumbnail(course.thumbnailUrl || '');
      setIsPublished(course.isPublished !== false);
    }
  }, [course, editMode]);

  const handleEdit = () => { setBackup({ title, category, description, thumbnail, isPublished }); setEditMode(true); };
  const handleCancel = () => {
    setTitle(backup.title); setCategory(backup.category); setDescription(backup.description);
    setThumbnail(backup.thumbnail); setIsPublished(backup.isPublished); setEditMode(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await updateCourse(courseId, { title, category, description, thumbnailUrl: thumbnail, isPublished });
      setEditMode(false);
      if (onSaved) await onSaved();
    } catch (err) {
      console.error('Error updating course:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (file) { const r = new FileReader(); r.onloadend = () => setThumbnail(r.result); r.readAsDataURL(file); }
  };

  const labelClass = `block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-1`;
  const fieldClass = `w-full border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'} rounded-md px-3 py-2`;

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 mb-6`}>
      <div className="flex justify-end gap-2 mb-4">
        {!editMode ? (
          <EditButton onClick={handleEdit} />
        ) : (
          <>
            <button onClick={onDeleted} className="flex items-center gap-1 px-4 py-2 rounded-md bg-red-100 text-red-700 font-semibold hover:bg-red-200 dark:bg-red-900 dark:text-red-200 dark:hover:bg-red-800">
              <Trash2 className="h-4 w-4" /> Delete
            </button>
            <button onClick={handleCancel} className="flex items-center gap-1 px-4 py-2 rounded-md bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600">
              <X className="h-4 w-4" /> Cancel
            </button>
            <SaveButton onClick={handleSave} disabled={saving} />
          </>
        )}
      </div>

      <label className={labelClass}>Course Title</label>
      <input type="text" className={`${fieldClass} mb-4`} value={title} onChange={(e) => setTitle(e.target.value)} readOnly={!editMode} />

      <div className="flex gap-4 mb-4">
        <div className="w-1/2">
          <label className={labelClass}>Category</label>
          <select className={fieldClass} value={category} onChange={(e) => setCategory(e.target.value)} disabled={!editMode}>
            {categoryOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div className="w-1/2">
          <label className={labelClass}>Visibility</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => editMode && setIsPublished(true)} disabled={!editMode}
              className={`flex-1 px-4 py-2 rounded-md border transition-colors ${isPublished ? (darkMode ? 'bg-green-700 border-green-600 text-white' : 'bg-green-100 border-green-500 text-green-700') : (darkMode ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-500')} ${!editMode ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-80'}`}>
              Published
            </button>
            <button type="button" onClick={() => editMode && setIsPublished(false)} disabled={!editMode}
              className={`flex-1 px-4 py-2 rounded-md border transition-colors ${!isPublished ? (darkMode ? 'bg-yellow-700 border-yellow-600 text-white' : 'bg-yellow-100 border-yellow-500 text-yellow-700') : (darkMode ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-500')} ${!editMode ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-80'}`}>
              Draft
            </button>
          </div>
        </div>
      </div>

      <label className={labelClass}>Description</label>
      <textarea className={`${fieldClass} min-h-[100px] mb-4`} value={description} onChange={(e) => setDescription(e.target.value)} readOnly={!editMode} />

      <label className={labelClass}>Thumbnail Image</label>
      {thumbnail && <img src={thumbnail} alt="Course Thumbnail" className="mt-2 w-full h-32 object-cover rounded" />}
      {editMode && <input type="file" accept="image/*" className={`w-full mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} onChange={handleThumbnailChange} />}
    </div>
  );
};

// ------------------------------- Container page -------------------------------

const CourseOverview = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedModules, setExpandedModules] = useState([]);
  const [expandedLessons, setExpandedLessons] = useState([]);

  const refresh = useCallback(async () => {
    const data = await getCourseById(courseId);
    if (data) {
      data.modules = (data.modules || []).map((m) => ({
        ...m,
        lessons: (m.lessons || []).sort((a, b) => (a.order || 0) - (b.order || 0))
      }));
    }
    setCourse(data);
    return data;
  }, [courseId]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const data = await refresh();
        if (active && data) setExpandedModules(data.modules.map((m) => m.id));
        setError(null);
      } catch (err) {
        console.error('Error fetching course:', err);
        if (active) setError('Failed to load course data. Please try again later.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [refresh]);

  const toggleModule = (id) => setExpandedModules((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));
  const toggleLesson = (id) => setExpandedLessons((prev) => (prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]));

  const handleReorderModule = async (index, dir) => {
    if (!course) return;
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= course.modules.length) return;
    const ids = course.modules.map((m) => m.id);
    [ids[index], ids[newIndex]] = [ids[newIndex], ids[index]];
    try {
      await reorderModules(courseId, ids);
      await refresh();
    } catch (err) {
      console.error('Error reordering modules:', err);
    }
  };

  const handleReorderLesson = async (moduleId, index, dir) => {
    if (!course) return;
    const module = course.modules.find((m) => m.id === moduleId);
    if (!module) return;
    const newIndex = index + dir;
    if (newIndex < 0 || newIndex >= module.lessons.length) return;
    const ids = module.lessons.map((l) => l.id);
    [ids[index], ids[newIndex]] = [ids[newIndex], ids[index]];
    try {
      await reorderLessons(courseId, moduleId, ids);
      await refresh();
    } catch (err) {
      console.error('Error reordering lessons:', err);
    }
  };

  const handleAddModule = async () => {
    try {
      const newModule = await addModuleToCourse(courseId, { title: 'New Module', order: (course?.modules?.length || 0) + 1 });
      await refresh();
      setExpandedModules((prev) => [...prev, newModule.id]);
    } catch (err) {
      console.error('Error adding module:', err);
    }
  };

  const handleAddLesson = async (moduleId, lessonCount) => {
    try {
      const newLesson = await addLessonToModule(courseId, moduleId, { title: 'New Lesson', status: 'draft', order: lessonCount + 1 });
      await refresh();
      setExpandedLessons((prev) => [...prev, newLesson.id]);
    } catch (err) {
      console.error('Error adding lesson:', err);
    }
  };

  const handleDeleteCourse = async () => {
    if (!window.confirm('Are you sure you want to delete this entire course template?')) return;
    try {
      await deleteCourse(courseId);
      navigate('/crm/course-templates');
    } catch (err) {
      console.error('Error deleting course:', err);
      setError('Failed to delete course. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <BBALoader size={48} className="mx-auto" />
          <p className={`mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading course data...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>{error || 'Course not found.'}</p>
          <button onClick={() => window.location.reload()} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 relative z-0">
      <PortalBackground />
      <div className="relative max-w-4xl mx-auto">
        {/* Top Row */}
        <div className="flex items-center justify-between mb-6 gap-2">
          <BackButton onClick={() => navigate('/crm/course-templates')} />
          <h1 className={`text-3xl font-bold tracking-tight text-center flex-1 text-gray-900 dark:text-white`}>{course.title}</h1>
          <div className="w-10 flex-shrink-0" />
        </div>

        {/* Course details */}
        <CourseDetailsCard courseId={courseId} course={course} darkMode={darkMode} onSaved={refresh} onDeleted={handleDeleteCourse} />

        {/* Modules + Lessons */}
        <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Modules & Lessons</h2>
            <button onClick={handleAddModule} className={`flex items-center gap-1 px-3 py-1.5 rounded-md ${darkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              <Plus className="h-4 w-4" /> Add Module
            </button>
          </div>

          {course.modules.length === 0 ? (
            <p className={`text-sm italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No modules yet. Click “Add Module” to get started.</p>
          ) : (
            <div className="space-y-3">
              {course.modules.map((module, index) => (
                <ModuleAccordion
                  key={module.id}
                  courseId={courseId}
                  courseTitle={course.title}
                  module={module}
                  index={index}
                  total={course.modules.length}
                  darkMode={darkMode}
                  expanded={expandedModules.includes(module.id)}
                  onToggle={toggleModule}
                  onReorderModule={handleReorderModule}
                  expandedLessons={expandedLessons}
                  onToggleLesson={toggleLesson}
                  onReorderLesson={handleReorderLesson}
                  onAddLesson={handleAddLesson}
                  refresh={refresh}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CourseOverview;
